import React, { useEffect, useState } from "react";
import { Newspaper, ChevronRight, Clock, ImageIcon } from "lucide-react";
import { api } from "../../lib/api";
import { C, fmtWaktu, namaKota } from "./ui";
import { Sheet, Empty, chipStyle } from "./Sheet";

// Sampul artikel; bila belum ada gambar → placeholder lembut (siap untuk unggah gambar dari admin/walikota)
function Cover({ src, h, radius = 18, children }) {
  return (
    <div style={{ position: "relative", height: h, borderRadius: radius, overflow: "hidden", background: "linear-gradient(135deg, #f4f1e6 0%, #e6e9ff 100%)", flex: "none" }}>
      {src
        ? <img src={api.mediaUrl(src)} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        : <ImageIcon size={Math.min(26, h / 3)} color="rgba(0,0,205,0.35)" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} />}
      {children}
    </div>
  );
}

const Rubrik = ({ a }) => <span style={chipStyle(a.city_id ? "green" : "blue")}>{a.city_id ? `Kota ${namaKota(a.city_id)}` : "Jawsel"}</span>;
const Waktu = ({ iso }) => <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: C.sub }}><Clock size={11} /> {fmtWaktu(iso)}</span>;

// Sheet Koran: headline besar (artikel terbaru) + daftar kompak berthumbnail → ketuk untuk baca penuh
export function KoranSheet({ cityId, onClose }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.publicKoran(cityId).then(d => setRows(d.articles || [])).catch(() => setRows([])); }, [cityId]);

  if (sel) return <Artikel a={sel} onBack={() => setSel(null)} onClose={onClose} />;
  const [utama, ...sisa] = rows || [];

  return (
    <Sheet testid="koran-sheet" title="Koran Jawsel" sub={`Redaksi Jawsel & Kota ${namaKota(cityId)}`} icon={Newspaper} onClose={onClose}>
      {rows === null && <p style={{ color: C.sub, fontSize: 13, padding: "8px 2px" }}>Memuat koran...</p>}
      {rows && rows.length === 0 && <Empty icon={Newspaper} testid="koran-empty" text="Belum ada berita. Redaksi Jawsel dan walikota akan menulis di sini." />}
      {utama && (
        <button data-testid={`koran-${utama.id}`} onClick={() => setSel(utama)}
          style={{ display: "block", width: "100%", textAlign: "left", border: 0, padding: 0, background: "transparent", cursor: "pointer", color: C.ink, fontFamily: "inherit" }}>
          <Cover src={utama.cover} h={190} radius={22}>
            <span style={{ position: "absolute", left: 12, top: 12, display: "inline-flex", gap: 6 }}><Rubrik a={utama} /><span style={chipStyle("gray")}>Terbaru</span></span>
          </Cover>
          <h2 data-testid="koran-headline" style={{ margin: "12px 2px 6px", fontSize: 20, fontWeight: 800, letterSpacing: -0.4, lineHeight: 1.25 }}>{utama.title}</h2>
          <p style={{ margin: "0 2px", fontSize: 13, color: C.sub, lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{utama.body}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 2px 0", fontSize: 12, color: C.sub }}>
            <b style={{ color: C.ink, fontWeight: 700 }}>{utama.author?.nama || "Redaksi Jawsel"}</b> <Waktu iso={utama.published_at} />
          </div>
        </button>
      )}
      {sisa.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <p style={{ margin: "0 2px 8px", fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: C.sub }}>Berita lainnya</p>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {sisa.map(a => (
              <button key={a.id} data-testid={`koran-${a.id}`} onClick={() => setSel(a)}
                style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", border: 0, borderTop: `1px solid ${C.line}`, padding: "12px 2px", background: "transparent", cursor: "pointer", color: C.ink, fontFamily: "inherit" }}>
                <div style={{ width: 76 }}><Cover src={a.cover} h={60} radius={14} /></div>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}><Rubrik a={a} /><Waktu iso={a.published_at} /></span>
                  <b style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: 14, lineHeight: 1.3, letterSpacing: -0.2 }}>{a.title}</b>
                </span>
                <ChevronRight size={16} color={C.sub} style={{ flex: "none" }} />
              </button>
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
}

// Tampilan baca artikel: sampul lebar, judul, penulis, isi dengan tipografi nyaman; galeri gambar (jika nanti ada `images`)
function Artikel({ a, onBack, onClose }) {
  const tgl = new Date(a.published_at || a.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const paras = String(a.body || "").split(/\n{2,}/);
  return (
    <Sheet testid="koran-article" title={a.title} sub={`${a.author?.nama || "Redaksi Jawsel"} · ${fmtWaktu(a.published_at)}`} onBack={onBack} onClose={onClose}>
      {a.cover && <Cover src={a.cover} h={210} radius={20} />}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: a.cover ? 14 : 4 }}><Rubrik a={a} /><span style={{ fontSize: 11.5, color: C.sub }}>{tgl}</span></div>
      <h2 data-testid="article-title" style={{ margin: "10px 0 10px", fontSize: 22, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.22 }}>{a.title}</h2>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: `1px solid ${C.line}`, marginBottom: 14 }}>
        <span style={{ width: 32, height: 32, borderRadius: 99, background: a.city_id ? "#d6f2e2" : C.blueSoft, color: a.city_id ? "#189a5b" : C.blue, display: "grid", placeItems: "center", fontWeight: 800, fontSize: 13 }}>{(a.author?.nama || "J").charAt(0).toUpperCase()}</span>
        <span style={{ fontSize: 12.5 }}><b>{a.author?.nama || "Redaksi Jawsel"}</b><span style={{ color: C.sub }}> · {a.author?.role === "mayor" ? "Walikota" : "Redaksi"}</span></span>
      </div>
      <div data-testid="article-body" style={{ color: C.ink }}>
        {paras.map((p, i) => (
          <p key={i} style={{ margin: "0 0 14px", fontSize: i === 0 ? 16 : 15, lineHeight: 1.75, whiteSpace: "pre-wrap", fontWeight: i === 0 ? 500 : 400 }}>{p}</p>
        ))}
      </div>
      {Array.isArray(a.images) && a.images.length > 0 && (
        <div data-testid="article-gallery" style={{ display: "grid", gridTemplateColumns: a.images.length > 1 ? "1fr 1fr" : "1fr", gap: 8, marginTop: 4 }}>
          {a.images.map((src, i) => <Cover key={i} src={src} h={140} radius={16} />)}
        </div>
      )}
    </Sheet>
  );
}
